import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useLocation, Link } from "react-router-dom";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  Toolbar,
  Typography,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Divider,
  Menu,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import {
  ExpandLess,
  ExpandMore,
  Logout,
  SettingsSuggest,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import { useUserInfo } from "@/context/UserInfoContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useSidebar } from "@/context/SidebarContext";
import {
  FontNavbarProjectName,
  FontNavbarParagraph,
  FontSideBarParagraph,
} from "@/styles/globalstyles";
import menu from "./menu";

const drawerWidth = 240;
const environment = import.meta.env.VITE_ENVIRONMENT || "DEV";
const priceKeys = ["/base-price", "/exchange-rate", "/return-scrap"];
const supplierKeys = ["/breakdown", "/material-supplier"];
const masterKeys = [
  "/master-material",
  "/master-process",
  "/master-system",
  "/master-buyer-boss",
];
const paKeys = ["/pa-part", "/pa-supplier"];

export default function NavBarWithDrawer() {
  const [anchorElUser, setAnchorElUser] = useState(false);
  const userInfo = useUserInfo();
  const { canView } = usePermissions();
  const { instance } = useMsal();
  const location = useLocation();
  const path = location.pathname.toLocaleLowerCase();
  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const [isMasterOpen, setIsMasterOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [isSupplierOpen, setIsSupplierOpen] = useState(false);
  const [isPaOpen, setIsPaOpen] = useState(false);

  const priceMenuKeys = ["baseprice", "exchange.rate", "return.scrap"];
  const supplierMenuKeys = ["breakdown", "material.supplier"];
  const paMenuKeys = ["pa.part", "pa.supplier"];
  const visiblePriceItems = menu.filter(
    (item) => priceMenuKeys.includes(item.key) && canView(item.key),
  );
  const visibleSupplierItems = menu.filter(
    (item) => supplierMenuKeys.includes(item.key) && canView(item.key),
  );
  const visibleMasterItems = menu.filter(
    (item) => item.key.startsWith("master") && canView(item.key),
  );
  const visiblePaItems = menu.filter(
    (item) => paMenuKeys.includes(item.key) && canView(item.key),
  );

  const handleOpenUserMenu = () => {
    setAnchorElUser(!anchorElUser);
  };

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    if (!isSidebarOpen) {
      setIsMasterOpen(false);
      setIsPriceOpen(false);
      setIsSupplierOpen(false);
      setIsPaOpen(false);
    } else {
      if (priceKeys.some((p) => path.startsWith(p))) setIsPriceOpen(true);
      if (supplierKeys.some((p) => path.startsWith(p))) setIsSupplierOpen(true);
      if (masterKeys.some((p) => path.startsWith(p))) setIsMasterOpen(true);
      if (paKeys.some((p) => path.startsWith(p))) setIsPaOpen(true);
    }
  }, [isSidebarOpen, path]);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: environment === "PROD" ? "#00A8A9" : "secondary.main",
          boxShadow: "none",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            sx={{
              mr: 2,
              marginRight: { xs: 1.5, sm: 3 },
              paddingLeft: { xs: 1.5, sm: 0.6 },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              alignItems: "center",
              width: "auto",
              height: "100%",
              display: {
                md: "flex",
                sm: "none",
                xs: "none",
              },
            }}
          >
            <img
              aria-hidden
              src="/kubota-logo.png"
              alt="kubota logo"
              width={120}
              height={50}
              style={{
                maxWidth: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              backgroundColor: "#C1C1C1",
              margin: "10px 10px",
              height: "40px",
              marginLeft: 3,
              marginRight: 3,
              width: 2,
              borderRadius: 10,
              display: {
                md: "flex",
                sm: "none",
                xs: "none",
              },
            }}
          />
          <Typography
            variant="h6"
            noWrap
            sx={{ ...FontNavbarProjectName, fontSize: "1.5rem" }}
          >
            AMPA System {environment === "PROD" ? "" : `(${environment})`}
          </Typography>
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={handleOpenUserMenu}
          >
            <Avatar
              sx={{
                bgcolor: "white",
                color: "#009688",
                mr: { xs: 0, sm: 0, md: 1 },
                width: { xs: 30, sm: 35 },
                height: { xs: 30, sm: 35 },
                "& img": {
                  objectPosition: "center 25%",
                },
              }}
              src={userInfo?.user_image || undefined}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                sx={{
                  ...FontNavbarParagraph,
                  color: "white",
                  display: { xs: "none", sm: "none", md: "initial" },
                }}
              >
                {userInfo?.name || "Firstname Lastname"} |{" "}
                {userInfo?.user_id || "userID"}
              </Typography>
              <Typography
                sx={{
                  ...FontNavbarParagraph,
                  fontWeight: 700,
                  color: "white",
                  display: { xs: "none", sm: "none", md: "initial" },
                }}
              >
                {userInfo?.groups?.join(", ") || "userID"}
              </Typography>
            </Box>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleOpenUserMenu}
            >
              <ListItemButton
                key={"Key"}
                sx={{ color: "#B6B6B6", width: "100%" }}
                onClick={handleLogout}
              >
                <Box
                  sx={{ width: "35%", display: "flex", alignItems: "center" }}
                >
                  {isDesktop ? (
                    <ListItemIcon>
                      <Logout />
                    </ListItemIcon>
                  ) : null}
                </Box>
                <Box sx={{ width: "65%" }}>
                  <Typography>{"ออกจากระบบ"}</Typography>
                </Box>
              </ListItemButton>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="persistent"
        anchor="left"
        open={true}
        sx={{
          width: isSidebarOpen ? drawerWidth : 60,
          flexShrink: 0,
          transition: "width 0.3s",
          [`& .MuiDrawer-paper`]: {
            width: isSidebarOpen ? drawerWidth : 60,
            boxSizing: "border-box",
            transition: "width 0.3s",
            overflow: "hidden",
          },
          zIndex: 1100,
        }}
      >
        <Toolbar />
        <Box>
          <Divider />
          <List sx={{ paddingTop: 1 }}>
            {/* Home Menu */}
            {menu
              .filter((item) => {
                return canView(item.key) && item.key === "home";
              })
              .map((item) => {
                const Icon = (MuiIcons as Record<string, React.ElementType>)[
                  item.icon
                ];
                const isActive = path === item.path;
                return (
                  <Tooltip
                    key={item.key}
                    title={!isSidebarOpen ? item.name : ""}
                    placement="right"
                  >
                    <ListItemButton
                      key={item.key}
                      component={Link}
                      to={item.path}
                      sx={{
                        height: 40,
                        paddingTop: 1,
                        paddingBottom: 1,
                        backgroundColor: isActive ? "#ED6F2D" : "transparent",
                        boxShadow: isActive ? "inset 4px 0 0 #B8541E" : "none",
                        borderRadius: isActive ? "0 4px 4px 0" : "none",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                      onClick={() => {
                        if (isSidebarOpen) {
                          setIsSidebarOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isSidebarOpen ? 3 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <Icon
                          sx={{
                            color: isActive
                              ? "white"
                              : theme.palette.primary.main,
                            transition: "color 0.2s ease-in-out",
                            ".MuiListItemButton-root:hover &": {
                              color: isActive && "#ED6F2D",
                            },
                          }}
                        />
                      </ListItemIcon>
                      {isSidebarOpen && (
                        <ListItemText
                          sx={{ ...FontSideBarParagraph, textWrap: "nowrap" }}
                          primary={item.name}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            {/* Price Management Group */}
            {visiblePriceItems.length > 0 && (
              <>
            <ListItemButton
              key={"price"}
              sx={{
                height: 40,
                paddingTop: 1,
                paddingBottom: 1,
                boxShadow:
                  path.startsWith("/base-price") ||
                  path.startsWith("/exchange-rate") ||
                  path.startsWith("/return-scrap")
                    ? `inset 4px 0 0 ${theme.palette.primary.main}`
                    : "none",
                borderRadius:
                  path.startsWith("/base-price") ||
                  path.startsWith("/exchange-rate") ||
                  path.startsWith("/return-scrap")
                    ? "0 4px 4px 0"
                    : "none",
                textDecoration: "none",
                color: "inherit",
              }}
              onClick={() => {
                if (!isPriceOpen) {
                  setIsSidebarOpen(true);
                }
                setIsPriceOpen(!isPriceOpen);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isSidebarOpen ? 3 : "auto",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <MuiIcons.PriceChange
                  sx={{
                    color: theme.palette.primary.main,
                    transition: "color 0.2s ease-in-out",
                    ".MuiListItemButton-root:hover &": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
                {!isPriceOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -1,
                      right: -1,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      border: "1.5px solid white",
                    }}
                  />
                )}
              </ListItemIcon>
              {isSidebarOpen && (
                <ListItemText
                  sx={{
                    ...FontSideBarParagraph,
                    textWrap: "nowrap",
                    fontWeight: 700,
                  }}
                  primary="Price Management"
                />
              )}
              {isSidebarOpen && (isPriceOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
            <Collapse in={isPriceOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {visiblePriceItems
                  .map((item) => {
                    const Icon = (
                      MuiIcons as Record<string, React.ElementType>
                    )[item.icon];
                    let isActive = path
                      .toLowerCase()
                      .startsWith(item.path.toLowerCase());
                    if (item.path === "/") {
                      isActive = path === item.path;
                    } else {
                      isActive = path
                        .toLowerCase()
                        .startsWith(item.path.toLowerCase());
                    }
                    return (
                      <Tooltip
                        key={item.key}
                        title={!isSidebarOpen ? item.name : ""}
                        placement="right"
                      >
                        <ListItemButton
                          key={item.key}
                          component={Link}
                          to={item.path}
                          sx={{
                            height: 40,
                            paddingTop: 1,
                            paddingBottom: 1,
                            paddingLeft: isSidebarOpen ? 4 : "auto",
                            backgroundColor: isActive
                              ? "#ED6F2D"
                              : "transparent",
                            boxShadow: isActive
                              ? "inset 4px 0 0 #B8541E"
                              : "none",
                            borderRadius: isActive ? "0 4px 4px 0" : "none",
                            textDecoration: "none",
                            color: "inherit",
                          }}
                          onClick={() => {
                            if (isSidebarOpen) {
                              setIsSidebarOpen(false);
                            }
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: isSidebarOpen ? 3 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <Icon
                              sx={{
                                color: isActive
                                  ? "white"
                                  : theme.palette.primary.main,
                                transition: "color 0.2s ease-in-out",
                                ".MuiListItemButton-root:hover &": {
                                  color: isActive && "#ED6F2D",
                                },
                              }}
                            />
                          </ListItemIcon>
                          {isSidebarOpen && (
                            <ListItemText
                              sx={{
                                ...FontSideBarParagraph,
                                textWrap: "nowrap",
                              }}
                              primary={item.name}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
              </List>
            </Collapse>
              </>
            )}
            {visibleSupplierItems.length > 0 && (
              <>
            <ListItemButton
              key={"supplier"}
              sx={{
                height: 40,
                paddingTop: 1,
                paddingBottom: 1,
                boxShadow:
                  path.startsWith("/breakdown") ||
                  path.startsWith("/material-supplier")
                    ? `inset 4px 0 0 ${theme.palette.primary.main}`
                    : "none",
                borderRadius:
                  path.startsWith("/breakdown") ||
                  path.startsWith("/material-supplier")
                    ? "0 4px 4px 0"
                    : "none",
                textDecoration: "none",
                color: "inherit",
              }}
              onClick={() => {
                if (!isSupplierOpen) {
                  setIsSidebarOpen(true);
                }
                setIsSupplierOpen(!isSupplierOpen);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isSidebarOpen ? 3 : "auto",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <MuiIcons.LocalShipping
                  sx={{
                    color: theme.palette.primary.main,
                    transition: "color 0.2s ease-in-out",
                    ".MuiListItemButton-root:hover &": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
                {!isSupplierOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -1,
                      right: -1,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      border: "1.5px solid white",
                    }}
                  />
                )}
              </ListItemIcon>
              {isSidebarOpen && (
                <ListItemText
                  sx={{
                    ...FontSideBarParagraph,
                    textWrap: "nowrap",
                    fontWeight: 700,
                  }}
                  primary="Supplier Management"
                />
              )}
              {isSidebarOpen &&
                (isSupplierOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
            <Collapse in={isSupplierOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {visibleSupplierItems
                  .map((item) => {
                    const Icon = (
                      MuiIcons as Record<string, React.ElementType>
                    )[item.icon];
                    let isActive = path
                      .toLowerCase()
                      .startsWith(item.path.toLowerCase());
                    if (item.path === "/") {
                      isActive = path === item.path;
                    } else {
                      isActive = path
                        .toLowerCase()
                        .startsWith(item.path.toLowerCase());
                    }
                    return (
                      <Tooltip
                        key={item.key}
                        title={!isSidebarOpen ? item.name : ""}
                        placement="right"
                      >
                        <ListItemButton
                          key={item.key}
                          component={Link}
                          to={item.path}
                          sx={{
                            height: 40,
                            paddingTop: 1,
                            paddingBottom: 1,
                            paddingLeft: isSidebarOpen ? 4 : "auto",
                            backgroundColor: isActive
                              ? "#ED6F2D"
                              : "transparent",
                            boxShadow: isActive
                              ? "inset 4px 0 0 #B8541E"
                              : "none",
                            borderRadius: isActive ? "0 4px 4px 0" : "none",
                            textDecoration: "none",
                            color: "inherit",
                          }}
                          onClick={() => {
                            if (isSidebarOpen) {
                              setIsSidebarOpen(false);
                            }
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: isSidebarOpen ? 3 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <Icon
                              sx={{
                                color: isActive
                                  ? "white"
                                  : theme.palette.primary.main,
                                transition: "color 0.2s ease-in-out",
                                ".MuiListItemButton-root:hover &": {
                                  color: isActive && "#ED6F2D",
                                },
                              }}
                            />
                          </ListItemIcon>
                          {isSidebarOpen && (
                            <ListItemText
                              sx={{
                                ...FontSideBarParagraph,
                                textWrap: "nowrap",
                              }}
                              primary={item.name}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
              </List>
            </Collapse>
              </>
            )}
            {/* Other Menu Items (before PA RFQ) */}
            {menu
              .filter((item) => {
                const priceKeys = ["baseprice", "exchange.rate", "return.scrap"];
                const supplierKeys = ["breakdown", "material.supplier"];
                const paKeys = ["pa.part", "pa.supplier"];
                const afterPaKeys = ["role", "log", "admin"];
                return (
                  canView(item.key) &&
                  item.key !== "home" &&
                  !item.key.startsWith("master") &&
                  !priceKeys.includes(item.key) &&
                  !supplierKeys.includes(item.key) &&
                  !paKeys.includes(item.key) &&
                  !afterPaKeys.includes(item.key)
                );
              })
              .map((item) => {
                const Icon = (MuiIcons as Record<string, React.ElementType>)[
                  item.icon
                ];
                let isActive = path
                  .toLowerCase()
                  .startsWith(item.path.toLowerCase());
                if (item.path === "/") {
                  isActive = path === item.path;
                } else {
                  isActive = path
                    .toLowerCase()
                    .startsWith(item.path.toLowerCase());
                }
                return (
                  <Tooltip
                    key={item.key}
                    title={!isSidebarOpen ? item.name : ""}
                    placement="right"
                  >
                    <ListItemButton
                      key={item.key}
                      component={Link}
                      to={item.path}
                      sx={{
                        height: 40,
                        paddingTop: 1,
                        paddingBottom: 1,
                        backgroundColor: isActive ? "#ED6F2D" : "transparent",
                        boxShadow: isActive ? "inset 4px 0 0 #B8541E" : "none",
                        borderRadius: isActive ? "0 4px 4px 0" : "none",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                      onClick={() => {
                        if (isSidebarOpen) {
                          setIsSidebarOpen(false);
                        }
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isSidebarOpen ? 3 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <Icon
                          sx={{
                            color: isActive
                              ? "white"
                              : item.icon == "SettingsOutlined"
                                ? theme.palette.secondary.main
                                : theme.palette.primary.main,
                            transition: "color 0.2s ease-in-out",
                            ".MuiListItemButton-root:hover &": {
                              color: isActive && "#ED6F2D",
                            },
                          }}
                        />
                      </ListItemIcon>
                      {isSidebarOpen && (
                        <ListItemText
                          sx={{ ...FontSideBarParagraph, textWrap: "nowrap" }}
                          primary={item.name}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            {/* PA RFQ Group */}
            {visiblePaItems.length > 0 && (
              <>
            <ListItemButton
              key={"pa-rfq"}
              sx={{
                height: 40,
                paddingTop: 1,
                paddingBottom: 1,
                boxShadow:
                  path.startsWith("/pa-part") || path.startsWith("/pa-supplier")
                    ? `inset 4px 0 0 ${theme.palette.primary.main}`
                    : "none",
                borderRadius:
                  path.startsWith("/pa-part") || path.startsWith("/pa-supplier")
                    ? "0 4px 4px 0"
                    : "none",
                textDecoration: "none",
                color: "inherit",
              }}
              onClick={() => {
                if (!isPaOpen) {
                  setIsSidebarOpen(true);
                }
                setIsPaOpen(!isPaOpen);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isSidebarOpen ? 3 : "auto",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <MuiIcons.RequestQuote
                  sx={{
                    color: theme.palette.primary.main,
                    transition: "color 0.2s ease-in-out",
                    ".MuiListItemButton-root:hover &": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
                {!isPaOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -1,
                      right: -1,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      border: "1.5px solid white",
                    }}
                  />
                )}
              </ListItemIcon>
              {isSidebarOpen && (
                <ListItemText
                  sx={{ ...FontSideBarParagraph, textWrap: "nowrap", fontWeight: 700 }}
                  primary="PA RFQ"
                />
              )}
              {isSidebarOpen && (isPaOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
            <Collapse in={isPaOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {visiblePaItems.map((item) => {
                  const Icon = (MuiIcons as Record<string, React.ElementType>)[item.icon];
                  const isActive = path.toLowerCase().startsWith(item.path.toLowerCase());
                  return (
                    <Tooltip
                      key={item.key}
                      title={!isSidebarOpen ? item.name : ""}
                      placement="right"
                    >
                      <ListItemButton
                        key={item.key}
                        component={Link}
                        to={item.path}
                        sx={{
                          height: 40,
                          paddingTop: 1,
                          paddingBottom: 1,
                          paddingLeft: isSidebarOpen ? 4 : "auto",
                          backgroundColor: isActive ? "#ED6F2D" : "transparent",
                          boxShadow: isActive ? "inset 4px 0 0 #B8541E" : "none",
                          borderRadius: isActive ? "0 4px 4px 0" : "none",
                          textDecoration: "none",
                          color: "inherit",
                        }}
                        onClick={() => {
                          if (isSidebarOpen) setIsSidebarOpen(false);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: isSidebarOpen ? 3 : "auto",
                            justifyContent: "center",
                          }}
                        >
                          <Icon
                            sx={{
                              color: isActive ? "white" : theme.palette.primary.main,
                              transition: "color 0.2s ease-in-out",
                              ".MuiListItemButton-root:hover &": {
                                color: isActive && "#ED6F2D",
                              },
                            }}
                          />
                        </ListItemIcon>
                        {isSidebarOpen && (
                          <ListItemText
                            sx={{ ...FontSideBarParagraph, textWrap: "nowrap" }}
                            primary={item.name}
                          />
                        )}
                      </ListItemButton>
                    </Tooltip>
                  );
                })}
              </List>
            </Collapse>
              </>
            )}
            {/* Other Menu Items (after PA RFQ) */}
            {menu
              .filter((item) => {
                const afterPaKeys = ["role", "log", "admin"];
                return canView(item.key) && afterPaKeys.includes(item.key);
              })
              .map((item) => {
                const Icon = (MuiIcons as Record<string, React.ElementType>)[item.icon];
                const isActive = path.toLowerCase().startsWith(item.path.toLowerCase());
                return (
                  <Tooltip
                    key={item.key}
                    title={!isSidebarOpen ? item.name : ""}
                    placement="right"
                  >
                    <ListItemButton
                      key={item.key}
                      component={Link}
                      to={item.path}
                      sx={{
                        height: 40,
                        paddingTop: 1,
                        paddingBottom: 1,
                        backgroundColor: isActive ? "#ED6F2D" : "transparent",
                        boxShadow: isActive ? "inset 4px 0 0 #B8541E" : "none",
                        borderRadius: isActive ? "0 4px 4px 0" : "none",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                      onClick={() => { if (isSidebarOpen) setIsSidebarOpen(false); }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: isSidebarOpen ? 3 : "auto", justifyContent: "center" }}>
                        <Icon
                          sx={{
                            color: isActive ? "white" : theme.palette.primary.main,
                            transition: "color 0.2s ease-in-out",
                            ".MuiListItemButton-root:hover &": { color: isActive && "#ED6F2D" },
                          }}
                        />
                      </ListItemIcon>
                      {isSidebarOpen && (
                        <ListItemText
                          sx={{ ...FontSideBarParagraph, textWrap: "nowrap" }}
                          primary={item.name}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            {visibleMasterItems.length > 0 && (
              <>
            <ListItemButton
              key={"master"}
              sx={{
                height: 40,
                paddingTop: 1,
                paddingBottom: 1,
                boxShadow:
                  path.startsWith("/master-material") ||
                  path.startsWith("/master-process") ||
                  path.startsWith("/master-system") ||
                  path.startsWith("/master-buyer-boss")
                    ? "inset 4px 0 0 #B8541E"
                    : "none",
                borderRadius:
                  path.startsWith("/master-material") ||
                  path.startsWith("/master-process") ||
                  path.startsWith("/master-system") ||
                  path.startsWith("/master-buyer-boss")
                    ? "0 4px 4px 0"
                    : "none",
                textDecoration: "none",
                color: "inherit",
              }}
              onClick={() => {
                if (!isMasterOpen) {
                  setIsSidebarOpen(true);
                }
                setIsMasterOpen(!isMasterOpen);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isSidebarOpen ? 3 : "auto",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <SettingsSuggest
                  sx={{
                    color: theme.palette.secondary.main,
                    transition: "color 0.2s ease-in-out",
                    ".MuiListItemButton-root:hover &": {
                      color: "#ED6F2D",
                    },
                  }}
                />
                {!isMasterOpen && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -1,
                      right: -1,
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.secondary.main,
                      border: "1.5px solid white",
                    }}
                  />
                )}
              </ListItemIcon>
              {isSidebarOpen && (
                <ListItemText
                  sx={{
                    ...FontSideBarParagraph,
                    textWrap: "nowrap",
                    fontWeight: 700,
                  }}
                  primary="Master"
                />
              )}
              {isSidebarOpen &&
                (isMasterOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
            <Collapse in={isMasterOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {visibleMasterItems
                  .map((item) => {
                    const Icon = (
                      MuiIcons as Record<string, React.ElementType>
                    )[item.icon];
                    let isActive = path
                      .toLowerCase()
                      .startsWith(item.path.toLowerCase());
                    if (item.path === "/") {
                      isActive = path === item.path;
                    } else {
                      isActive = path
                        .toLowerCase()
                        .startsWith(item.path.toLowerCase());
                    }
                    return (
                      <Tooltip
                        key={item.key}
                        title={!isSidebarOpen ? item.name : ""}
                        placement="right"
                      >
                        <ListItemButton
                          key={item.key}
                          component={Link}
                          to={item.path}
                          sx={{
                            height: 40,
                            paddingTop: 1,
                            paddingBottom: 1,
                            paddingLeft: isSidebarOpen ? 4 : "auto",
                            backgroundColor: isActive
                              ? "#ED6F2D"
                              : "transparent",
                            boxShadow: isActive
                              ? "inset 4px 0 0 #B8541E"
                              : "none",
                            borderRadius: isActive ? "0 4px 4px 0" : "none",
                            textDecoration: "none",
                            color: "inherit",
                          }}
                          onClick={() => {
                            if (isSidebarOpen) {
                              setIsSidebarOpen(false);
                            }
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: isSidebarOpen ? 3 : "auto",
                              justifyContent: "center",
                            }}
                          >
                            <Icon
                              sx={{
                                color: isActive
                                  ? "white"
                                  : item.key?.startsWith("master")
                                    ? theme.palette.secondary.main
                                    : theme.palette.primary.main,
                                transition: "color 0.2s ease-in-out",
                                ".MuiListItemButton-root:hover &": {
                                  color: isActive && "#ED6F2D",
                                },
                              }}
                            />
                          </ListItemIcon>
                          {isSidebarOpen && (
                            <ListItemText
                              sx={{
                                ...FontSideBarParagraph,
                                textWrap: "nowrap",
                              }}
                              primary={item.name}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
              </List>
            </Collapse>
              </>
            )}
            <ListItemButton
              sx={{
                height: 40,
                paddingTop: 1,
                paddingBottom: 1,
                backgroundColor: "transparent",
                borderLeft: "none",
              }}
              onClick={() => {
                handleLogout();
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isSidebarOpen ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <MuiIcons.LogoutOutlined
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                />
              </ListItemIcon>
              {isSidebarOpen && (
                <ListItemText
                  sx={{ ...FontSideBarParagraph, whiteSpace: "nowrap" }}
                  primary={"Log out"}
                />
              )}
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
